
import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { UsersRound } from "lucide-react";
import { Club } from "@/hooks/useClubs";
import { motion } from "framer-motion";

interface ClubCardProps {
  club: Club;
  onJoinClick: () => void;
  isJoining: boolean;
}

const ClubCard: React.FC<ClubCardProps> = ({ club, onJoinClick, isJoining }) => {
  const navigate = useNavigate();

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on the button
    if ((e.target as HTMLElement).closest('button')) {
      console.log('Button clicked, not navigating');
      return;
    }
    console.log('Navigating to club details:', `/clubs/${club.id}`);
    navigate(`/clubs/${club.id}`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ 
        y: -5,
        scale: 1.02,
        transition: { duration: 0.2 }
      }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.3 }}
    >
      <Card 
        className="transition-all duration-300 hover:shadow-xl hover:shadow-primary/10 cursor-pointer border border-border/50 hover:border-primary/30 bg-gradient-to-br from-card to-card/80 backdrop-blur-sm" 
        onClick={handleCardClick}
      >
        <CardHeader className="pb-3">
          <div className="flex justify-between items-start">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
            >
              <CardTitle className="text-xl font-bold bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent">
                {club.name}
              </CardTitle>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              <Badge className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 transition-colors duration-200">
                {club.category}
              </Badge>
            </motion.div>
          </div>
        </CardHeader>
        <CardContent className="pb-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <CardDescription className="line-clamp-3 mb-4 text-muted-foreground/90">
              {club.description}
            </CardDescription>
          </motion.div>
          <motion.div 
            className="flex items-center text-sm text-muted-foreground"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <UsersRound className="h-4 w-4 mr-2 text-primary" />
            <span className="font-medium">{club.member_count || 0} members</span>
          </motion.div>
        </CardContent>
        <CardFooter>
          <motion.div
            className="w-full"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Button 
              className={`w-full transition-all duration-300 ${
                club.is_member 
                  ? "bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white shadow-lg hover:shadow-xl" 
                  : "bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary text-primary-foreground shadow-lg hover:shadow-xl"
              }`}
              onClick={(e) => {
                e.stopPropagation();
                onJoinClick();
              }}
              disabled={isJoining}
            >
              {isJoining ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="flex items-center"
                >
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full mr-2" />
                  Processing...
                </motion.div>
              ) : club.is_member ? (
                "Leave Club"
              ) : (
                "Join Club"
              )}
            </Button>
          </motion.div>
        </CardFooter>
      </Card>
    </motion.div>
  );
};

export default ClubCard;
